import React, { useEffect, useState, useRef } from "react";
import CounterViewModel from "../viewmodels/CounterViewModel";
import GenerateQuote from "../viewmodels/GenerateQuote";
import ImageViewModel from "../viewmodels/ImageViewModel";
import styled, { keyframes } from "styled-components";

const GalleryView = () => {
  const [images, setImages] = useState([]); // Array of fetched image URLs (in order)
  const [visibleImages, setVisibleImages] = useState([]); // Indices to trigger your fade-in
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [loading, setLoading] = useState(true); // Spinner active
  const [loadingText, setLoadingText] = useState("");
  const [dots, setDots] = useState(".");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expectedCount, setExpectedCount] = useState(0);

  const itemsPerPage = 12;

  const galleryRef = useRef(null);

  // Spinner: Show for 2 seconds (reset when currentPage changes)
  useEffect(() => {
    setLoading(true);
    const spinnerTimer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    const dotsInterval = setInterval(() => {
      setDots((prevDots) => (prevDots.length === 3 ? "." : prevDots + "."));
    }, 500);

    setLoadingText(GenerateQuote());

    return () => {
      clearTimeout(spinnerTimer);
      clearInterval(dotsInterval);
    };
  }, [currentPage]);

  // After spinner, sequentially fetch paginated images
  useEffect(() => {
    if (!loading) {
      let isCancelled = false;
      const loadImagesSequentially = async () => {
        try {
          const counter = await CounterViewModel.getCounter();
          if (isCancelled) return;
          const calculatedTotalPages = Math.ceil((counter + 1) / itemsPerPage);
          setTotalPages(calculatedTotalPages);
          
          const imageKeysToFetch = CounterViewModel.getPaginatedImageKeys(counter, currentPage, itemsPerPage);
          
          setExpectedCount(imageKeysToFetch.length);
          setImages([]);
          setVisibleImages([]);

          for (let i = 0; i < imageKeysToFetch.length; i++) {
            if (isCancelled) break;
            const imageKey = imageKeysToFetch[i];
            
            let { url, isCached } = await ImageViewModel.getSignedImageUrl(imageKey);
            if (isCancelled) break;

            // Preload to verify the image works (especially important for old cached URLs that might have expired)
            let imageIsValid = await new Promise((resolve) => {
              const img = new window.Image();
              img.src = url;
              if (img.complete) {
                resolve(true); // Image is valid and in disk cache
              } else {
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false); // Image failed to load (e.g., AWS 403 Expired Signature)
              }
            });

            if (!imageIsValid) {
              // Self-Healing Logic: The URL was rejected by AWS!
              // This handles both old JS session URLs AND 1-month-old 'force-cache' URLs.
              console.warn(`URL for image ${imageKey} expired or failed. Self-healing and refetching fresh from AWS...`);
              
              // 1. Wipe the bad signature from the global custom cache
              ImageViewModel.invalidate(imageKey);
              
              // 2. Ask AWS for a brand newly signed URL string, explicitly bypassing Chrome's disk cache
              const freshFetch = await ImageViewModel.getSignedImageUrl(imageKey, true);
              url = freshFetch.url;
              isCached = false; // We just downloaded this, so handle it like a fresh image

              // 3. Preload the new URL to guarantee sequence
              await new Promise((resolve) => {
                const retryImg = new window.Image();
                retryImg.src = url;
                if (retryImg.complete) resolve();
                else {
                  retryImg.onload = resolve;
                  retryImg.onerror = resolve;
                }
              });
            }

            if (isCancelled) break;
            // Append the fetched image URL
            setImages((prev) => [...prev, url]);
            
            // Give React a moment to render the DOM element before triggering the CSS fade
            if (isCancelled) break;
            await new Promise((resolve) => setTimeout(resolve, 50));
            if (isCancelled) break;
            setVisibleImages((prev) => [...prev, i]);

            // If the image was in the cache, cascade them instantly instead of waiting 700ms
            const heartbeatDelay = isCached ? 100 : 700;
            await new Promise((resolve) => setTimeout(resolve, heartbeatDelay));
          }
        } catch (error) {
          if (!isCancelled) console.error("Error during sequential fetch:", error);
        }
      };
      
      loadImagesSequentially();
      return () => {
        isCancelled = true;
      };
    }
  }, [loading, currentPage]);

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handleModalClose = () => {
    setSelectedImageIndex(null);
  };

  const goLeft = (e) => {
    e.stopPropagation();
    if (selectedImageIndex > 0) {
      setSelectedImageIndex((prev) => prev - 1);
    }
  };

  const goRight = (e) => {
    e.stopPropagation();
    if (selectedImageIndex < images.length - 1) {
      setSelectedImageIndex((prev) => prev + 1);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <PageNumber key={1} onClick={() => handlePageChange(1)}>
          1
        </PageNumber>
      );
      if (startPage > 2) {
        pages.push(<Ellipsis key="start-ellipsis">...</Ellipsis>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PageNumber
          key={i}
          $active={currentPage === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageNumber>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Ellipsis key="end-ellipsis">...</Ellipsis>);
      }
      pages.push(
        <PageNumber
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </PageNumber>
      );
    }

    return (
      <PaginationContainer>
        <PageButton
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          &lt;
        </PageButton>
        {pages}
        <PageButton
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          &gt;
        </PageButton>
      </PaginationContainer>
    );
  };

  return (
    <>
      {loading ? (
        <LoadingContainer>
          <LoadingText>
            {loadingText} {dots}
          </LoadingText>
          <LoadingSpinner />
        </LoadingContainer>
      ) : (
        <>
          <GalleryContainer ref={galleryRef}>
            {images.map((image, index) => (
              <GalleryCard
                key={index}
                className={visibleImages.includes(index) ? "visible" : ""}
                onClick={() =>
                  visibleImages.includes(index) && handleImageClick(index)
                }
              >
                {visibleImages.includes(index) && image && (
                  <GalleryImage src={image} alt={`Gallery ${index}`} />
                )}
              </GalleryCard>
            ))}
          </GalleryContainer>
          {visibleImages.length === expectedCount && expectedCount > 0 && renderPagination()}
        </>
      )}

      {selectedImageIndex !== null && (
        <Modal onClick={handleModalClose}>
          <LeftButton onClick={goLeft}>&lt;</LeftButton>
          <ModalImage
            src={images[selectedImageIndex]}
            alt={`Gallery ${selectedImageIndex}`}
            onClick={(e) => e.stopPropagation()}
          />
          <RightButton onClick={goRight}>&gt;</RightButton>
        </Modal>
      )}
    </>
  );
};

export default GalleryView;

// Your original CSS styles are preserved below:

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const GalleryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const GalleryCard = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  cursor: pointer;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 1.9s ease, transform 1.9s ease;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`;

const Button = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: white;
  font-size: 36px;
  font-weight: 900;
  cursor: pointer;
  z-index: 1001;

  &:hover {
    color: red;
  }
`;

const LeftButton = styled(Button)`
  left: 20%;
`;

const RightButton = styled(Button)`
  right: 20%;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const LoadingSpinner = styled.div`
  border: 8px solid #000;
  border-top: 8px solid #f00;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: ${spin} 1s linear infinite;
  margin-top: 20px;
`;

const LoadingText = styled.div`
  font-family: "Heiti SC", sans-serif;
  font-size: 20px;
  color: red;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin: 40px 0;
  padding-bottom: 20px;
`;

const PageButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  color: ${(props) => (props.disabled ? "#ccc" : "#000")};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  font-family: "Heiti SC", sans-serif;

  &:hover {
    color: red;
  }
`;

const PageNumber = styled.span`
  font-size: 18px;
  cursor: pointer;
  color: ${(props) => (props.$active ? "red" : "#000")};
  font-weight: ${(props) => (props.$active ? "bold" : "normal")};
  font-family: "Heiti SC", sans-serif;

  &:hover {
    color: red;
  }
`;

const Ellipsis = styled.span`
  font-size: 18px;
  color: #000;
  font-family: "Heiti SC", sans-serif;
`;
